import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MessageCircle, Truck, ShieldCheck, Clock } from "lucide-react";
import { Toaster } from "sonner";
import CarServiceLookupWidget from "./CarServiceLookupWidget";
import CarServiceBookingModal from "./CarServiceBookingModal";
import CarServicePriceCard from "./CarServicePriceCard";
import { CarServicePackageProvider } from "./CarServicePackageContext";
import { buildCarServiceWhatsAppHref } from "./whatsappConfig";
import { trackEvent } from "../../utils/gtag";

/**
 * DoorstepCarServiceModal
 *
 * In-website E2E booking flow for the Doorstep Car Service product.
 * Mirrors the /carservice landing page UX (lookup → lead capture → price
 * reveal) but lives entirely inside a modal on /IN — so the homepage
 * never has to lose users to the LP. The LP itself is kept as the
 * Google Ads destination; this modal is for the organic / on-site path.
 *
 * Composition:
 *   • CarServiceLookupWidget    → resolves a quoted_price from CRM
 *   • CarServiceBookingModal    → 2-step lead capture (form → OTP)
 *   • CarServicePriceCard       → revealed AFTER lead capture as the reward
 *
 * All wrapped in CarServicePackageProvider so the inner widgets pick up
 * the active GENERAL package from CRM the same way the LP does.
 */
const DoorstepCarServiceModal = ({ open, onClose }) => {
  const [lookupResult, setLookupResult] = useState(null); // { price, vehicle }
  const [leadModalOpen, setLeadModalOpen] = useState(false);
  const [leadCaptured, setLeadCaptured] = useState(false);

  const handleLookupComplete = (price, vehicle) => {
    setLookupResult({ price, vehicle });
    setLeadCaptured(false);
    setLeadModalOpen(true);
    trackEvent("doorstep_modal_lookup_complete");
  };

  const handleLeadSuccess = () => {
    setLeadCaptured(true);
    trackEvent("doorstep_modal_lead_captured");
  };

  const handleResetLookup = () => {
    setLookupResult(null);
    setLeadCaptured(false);
    setLeadModalOpen(false);
  };

  const handleCloseAll = () => {
    setLookupResult(null);
    setLeadCaptured(false);
    setLeadModalOpen(false);
    onClose?.();
  };

  return (
    <CarServicePackageProvider>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[60] bg-slate-950/70 backdrop-blur-sm overflow-y-auto"
            data-testid="doorstep-carservice-modal"
            onClick={handleCloseAll}
          >
            <div className="min-h-full flex items-start justify-center p-4 sm:p-6">
              <motion.div
                initial={{ y: 16, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 16, opacity: 0 }}
                transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                className="relative w-full max-w-2xl my-4 sm:my-8 bg-white rounded-3xl shadow-2xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 text-white px-6 sm:px-8 py-6 sm:py-7">
                  <button
                    type="button"
                    onClick={handleCloseAll}
                    aria-label="Close"
                    data-testid="doorstep-modal-close"
                    className="absolute top-4 right-4 h-9 w-9 inline-flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>

                  <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full border border-white/15 bg-white/10 text-[10px] font-bold uppercase tracking-[0.18em] text-blue-200 mb-3">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    Bengaluru · Live in 1 Hr
                  </div>

                  <h2
                    className="text-2xl sm:text-3xl font-bold tracking-tight leading-tight"
                    style={{ fontFamily: "Plus Jakarta Sans, sans-serif" }}
                  >
                    Doorstep Car Service
                  </h2>
                  <p className="text-sm sm:text-base text-blue-100/85 mt-1.5 max-w-md">
                    Get an instant, all-inclusive quote for your car. OEM parts ·
                    Certified mechanics · 1,000 km warranty.
                  </p>

                  <div className="mt-5 flex flex-wrap gap-2">
                    <Pill icon={Truck}        text="Free pickup & drop" />
                    <Pill icon={Clock}        text="Service in 1 hr" />
                    <Pill icon={ShieldCheck}  text="1,000 km warranty" />
                  </div>
                </div>

                {/* Body */}
                <div className="px-5 sm:px-7 py-6 sm:py-8 bg-slate-50/40">
                  {!leadCaptured && (
                    <CarServiceLookupWidget onPriceResolved={handleLookupComplete} />
                  )}

                  {leadCaptured && lookupResult?.price && (
                    <div className="space-y-4">
                      <div className="text-center">
                        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-[11px] font-bold uppercase tracking-[0.15em] text-emerald-700 mb-2">
                          ✓ Lead Confirmed
                        </div>
                        <h3 className="text-xl font-bold text-slate-900">
                          Your quote is ready
                        </h3>
                        <p className="text-sm text-slate-600 mt-1">
                          Our advisor will call you back within 30 mins.
                        </p>
                      </div>

                      <CarServicePriceCard
                        price={lookupResult.price}
                        vehicle={lookupResult.vehicle}
                        readOnly={true}
                        onReset={handleResetLookup}
                      />

                      <a
                        href={buildCarServiceWhatsAppHref({ clickType: "doorstep" })}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="doorstep-modal-whatsapp-cta"
                        className="block w-full text-center px-5 py-3.5 rounded-xl bg-green-600 hover:bg-green-700 text-white font-bold text-sm transition-all shadow-sm hover:shadow-md"
                      >
                        <MessageCircle className="inline h-4 w-4 mr-2 -mt-px" />
                        Skip the wait — Chat on WhatsApp
                      </a>
                    </div>
                  )}

                  {/* Trust strip */}
                  <div className="mt-6 pt-5 border-t border-slate-200/70 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[11px] font-semibold text-slate-500">
                    <span>★ 4.8 on Google · 3,200+ reviews</span>
                    <span className="hidden sm:inline">·</span>
                    <span>10,000+ services done</span>
                    <span className="hidden sm:inline">·</span>
                    <span>OEM Parts</span>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Lead-capture modal lives over the lookup view (z-[70] > z-[60]) */}
            <CarServiceBookingModal
              open={leadModalOpen}
              price={lookupResult?.price}
              vehicle={lookupResult?.vehicle}
              onLeadSuccess={handleLeadSuccess}
              onClose={() => setLeadModalOpen(false)}
            />

            {/* Sonner toaster scoped within the portal */}
            <Toaster position="top-center" richColors closeButton />
          </motion.div>
        )}
      </AnimatePresence>
    </CarServicePackageProvider>
  );
};

const Pill = ({ icon: Icon, text }) => (
  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/[0.08] border border-white/15 text-[11px] font-semibold text-blue-50">
    <Icon className="h-3.5 w-3.5 text-blue-300" />
    {text}
  </div>
);

export default DoorstepCarServiceModal;
